import { RecordContextProvider, useGetOne, useListContext, useRecordContext } from "react-admin";
import { InAppNotification } from "../providers/inAppNotificationsProvider";
import { Spinner } from "../misc/Spinner";
import { Avatar, Divider, List, ListItem, Typography, useTheme } from "@mui/material";
import { Column } from "../misc/Column";
import { Row } from "../misc/Row";
import { Fragment } from "react";

const _avatarSize = 48;
const _textWidth = 260;

type Variant = "card" | "tab";

export function InAppNotificationsList({ variant = "card" }: { variant?: Variant }) {
  const { data, isLoading, total } = useListContext<InAppNotification>();
  if (isLoading) {
    return <Spinner style={{ margin: "20px" }} />;
  }
  if (!data || data.length === 0 || total === 0) {
    return (
      <Typography
        variant="body2"
        style={{
          marginTop: "20px",
          height: "40px",
          marginBottom: "20px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        Keine In-App-Benachrichtiungen vorhanden.
      </Typography>
    );
  }
  const divider =
    variant === "card" ? (
      <Divider style={{ maxWidth: `${20 + _avatarSize + 15 + _textWidth + 20}px`, margin: "0 auto" }} />
    ) : null;
  return (
    <List>
      {data.map((inAppNotification, index) => (
        <Fragment key={index}>
          {index === 0 && divider}
          <RecordContextProvider value={inAppNotification}>
            <_InAppNotificationsListItem variant={variant} />
          </RecordContextProvider>
          {divider}
        </Fragment>
      ))}
    </List>
  );
}

function _InAppNotificationsListItem({ variant }: { variant: Variant }) {
  const inAppNotification = useRecordContext<InAppNotification>();
  const theme = useTheme();
  const { data: sender } = useGetOne(
    "users",
    { id: inAppNotification?.senderUid },
    { enabled: !!inAppNotification?.senderUid },
  );
  return (
    <>
      <ListItem disablePadding>
        <div
          style={{
            position: "relative",
            margin: variant === "card" ? "0 auto" : undefined,
            width: variant === "tab" ? "100%" : undefined,
          }}
        >
          <Row alignItems="center" style={{ height: "95px" }}>
            {inAppNotification.status?.state !== "read" ? <_UnreadIndicator /> : <div style={{ minWidth: "20px" }} />}
            <Avatar src={sender?.avatarUrl} style={{ width: "48px", height: "48px", marginRight: "15px" }} />
            <Column style={{ width: variant === "card" ? "200px" : "100%", minWidth: `${_textWidth}px` }}>
              <Typography
                variant="body1"
                style={{
                  fontSize: "16px",
                  lineHeight: "20px",
                  color: theme.palette.text.primary,
                  height: "20px",
                  maxHeight: "20px",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                {inAppNotification.title}
              </Typography>
              <div style={{ minHeight: "5px" }} />
              <Typography
                variant="body2"
                style={{
                  color: theme.palette.text.secondary,
                  fontSize: "12px",
                  lineHeight: "14px",
                  height: "28px",
                  maxHeight: "28px",
                  overflowWrap: "anywhere",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              >
                {inAppNotification.text}
              </Typography>
            </Column>
          </Row>
          <div
            style={{
              position: "absolute",
              right: "20px",
              top: "5px",
              color: theme.palette.text.secondary,
              fontSize: "12px",
              lineHeight: "14px",
            }}
          >
            {inAppNotification.createdAt.toFormat("dd.MM.yyyy")}
          </div>
        </div>
      </ListItem>
    </>
  );
}

function _UnreadIndicator() {
  const theme = useTheme();
  return (
    <div style={{ paddingLeft: "7px", paddingRight: "7", minWidth: "20px" }}>
      <svg height="6" width="6">
        <circle r="3" cx="3" cy="3" fill={theme.palette.error.main} />
      </svg>
    </div>
  );
}
